import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { MarketingSkillEntry } from '@sidegigllc/api/client'
import { fetchJson } from '../utils/fetchJson'
import { useSelectedMarket } from '../providers/Market'

/**
 * Single gated wages query shared across the QwickGo order grid, cart, and
 * checkout. Routed through the `/api/marketing/skills` edge proxy (not the SDK
 * directly) so the browser never sends `*.qwick.com` auth cookies to the API
 * and interferes with the logged-in app session.
 *
 * Held until market detection settles (`enabled: !isLoading`) so we fire once
 * with the resolved market (or no market as the national fallback) instead of
 * a throwaway no-market fetch that would flash before the real rates. Always
 * call this rather than hitting the endpoint directly so every consumer shares
 * the same query/cache key.
 */
export const useMarketingWages = () => {
  const { market, isLoading } = useSelectedMarket()
  const marketId = market?.marketId ?? null
  return useQuery<MarketingSkillEntry[]>({
    queryKey: ['marketingWages', marketId],
    queryFn: () =>
      fetchJson<MarketingSkillEntry[]>(
        `/api/marketing/skills${marketId ? `?market_id=${marketId}` : ''}`,
      ),
    enabled: !isLoading,
    placeholderData: keepPreviousData,
    staleTime: 24 * 60 * 60 * 1000,
  })
}
